import load from '#core/load';
import { LAYER_DEFAULTS, LAYER_OVERRIDES } from '#element/particle-cloud/particle-cloud-canvas';


const page_$ = '.particle-cloud-demo';
const canvas_$ = '[data-particle-cloud]';
const panel_class = 'particle-cloud-demo-controls';

const weight_keys = ['attract', 'repel', 'orbit', 'dampen'];

// Each control mirrors a key in LAYER_DEFAULTS.
// densityMultiplier is a custom shim: scales the auto-derived particle count.
// _weight_* are also custom: assembled into lociWeights below.
const groups = [
	{
		title: 'Density & particles',
		items: [
			{ key: 'densityMultiplier', def: 1, min: 0.1, max: 3, step: 0.05, tip: 'Scales the auto-derived particle count (1 ≈ 1 particle per px of layer diameter).' },
			{ key: 'minRadius', def: 1.5, min: 0.5, max: 10, step: 0.1, tip: 'Smallest particle radius (CSS px). Used in low-density regions.' },
			{ key: 'maxRadius', def: 6, min: 1, max: 20, step: 0.5, tip: 'Largest particle radius (CSS px). Used in high-density regions.' },
			{ key: 'speed', def: 1, min: 0, max: 5, step: 0.1, tip: 'Global movement multiplier applied to every per-frame displacement (noise, loci, center, boundary).' },
			{ key: 'baseVelocity', def: 0.05, min: 0, max: 0.5, step: 0.005, tip: 'Amplitude of the curl-noise drift force only (CSS px / frame). Other forces (loci, center) are independent.' },
		],
	},
	{
		title: 'Flow field',
		items: [
			{ key: 'noiseScale', def: 0.003, min: 0.0005, max: 0.02, step: 0.0005, tip: 'Spatial frequency of the flow field. Smaller = broader, smoother currents; larger = tighter, more chaotic swirls.' },
			{ key: 'noiseSpeed', def: 0.0002, min: 0, max: 0.002, step: 0.00005, tip: 'How fast the flow field drifts over time. Higher values make the field shift more quickly, creating restless motion.' },
		],
	},
	{
		title: 'Loci',
		items: [
			{ key: 'locusCount', def: 12, min: 0, max: 40, step: 1, tip: 'Number of loci alive at any given time. As one expires a new one spawns to replace it.' },
			{ key: 'locusMinLife', def: 3, min: 0.5, max: 15, step: 0.5, tip: 'Min lifespan (seconds). Each locus picks a random lifespan in this range; strength fades 0 → max → 0 over its life.' },
			{ key: 'locusMaxLife', def: 7, min: 0.5, max: 20, step: 0.5, tip: 'Max lifespan (seconds). Each locus picks a random lifespan in this range.' },
			{ key: 'locusMinInfluence', def: 0.2, min: 0, max: 1, step: 0.05, tip: 'Min influence radius as a fraction of the circle radius (0–1).' },
			{ key: 'locusMaxInfluence', def: 0.7, min: 0, max: 1, step: 0.05, tip: 'Max influence radius as a fraction of the circle radius (0–1).' },
			{ key: 'locusMinStrength', def: 0.02, min: 0, max: 0.2, step: 0.005, tip: 'Min peak strength a locus can reach (randomized per locus).' },
			{ key: 'locusMaxStrength', def: 0.04, min: 0, max: 0.2, step: 0.005, tip: 'Max peak strength a locus can reach (randomized per locus).' },
			{ key: 'dampenStrengthScale', def: 0.5, min: 0, max: 2, step: 0.05, tip: "Multiplier applied to 'dampen'-type loci strength only." },
			{ key: 'locusEdgeFalloffStart', def: 0.85, min: 0, max: 1, step: 0.05, tip: 'Distance from center (fraction of radius) where locus influence begins to fade. 0.85 = full strength inside 85% of radius, then linear drop toward locusEdgeFalloffMin at the edge.' },
			{ key: 'locusEdgeFalloffMin', def: 0.05, min: 0, max: 1, step: 0.05, tip: 'Fraction of full strength a locus keeps at the circle edge for particles still inside. Past the edge, particles are committed to fading out and feel no locus influence regardless. 0 = falls to zero at the edge.' },
		],
	},
	{
		title: 'Locus arc geometry',
		items: [
			{ key: 'locusArcMinRadius', def: 0.1, min: 0, max: 1, step: 0.05, tip: 'Min semi-major axis of the locus travel ellipse, as a fraction of the circle radius.' },
			{ key: 'locusArcMaxRadius', def: 0.5, min: 0, max: 1, step: 0.05, tip: 'Max semi-major axis of the locus travel ellipse, as a fraction of the circle radius.' },
			{ key: 'locusArcMinEccentricity', def: 0.4, min: 0.05, max: 1, step: 0.05, tip: 'Min eccentricity (semi-minor / semi-major). 1.0 = circular arc, 0.1 = very elongated.' },
			{ key: 'locusArcMaxEccentricity', def: 1.0, min: 0.05, max: 1, step: 0.05, tip: 'Max eccentricity (semi-minor / semi-major). 1.0 = circular arc.' },
			{ key: 'locusArcMinOffset', def: 0, min: 0, max: 1, step: 0.05, tip: 'Min distance of the arc center from the field center (0–1 of radius).' },
			{ key: 'locusArcMaxOffset', def: 0.6, min: 0, max: 1, step: 0.05, tip: 'Max distance of the arc center from the field center (0–1 of radius).' },
			{ key: 'locusArcMinSweep', def: Math.PI / 6, min: 0, max: Math.PI * 2, step: 0.05, tip: 'Min radians the locus travels along its arc over its life. π/4 ≈ 45°, π/2 ≈ 90°, π ≈ 180°.' },
			{ key: 'locusArcMaxSweep', def: Math.PI / 2, min: 0, max: Math.PI * 2, step: 0.05, tip: 'Max radians the locus travels along its arc over its life.' },
		],
	},
	{
		title: 'Locus type weights',
		items: [
			{ key: '_weight_attract', def: 1, min: 0, max: 5, step: 0.5, tip: 'Spawn weight for attract loci (pull particles toward the locus). 0 disables this type.' },
			{ key: '_weight_repel', def: 1, min: 0, max: 5, step: 0.5, tip: 'Spawn weight for repel loci (push particles away). 0 disables this type.' },
			{ key: '_weight_orbit', def: 2, min: 0, max: 5, step: 0.5, tip: 'Spawn weight for orbit loci (tangential force, particles circle the locus). 0 disables this type.' },
			{ key: '_weight_dampen', def: 1, min: 0, max: 5, step: 0.5, tip: 'Spawn weight for dampen loci (reduce velocity in their area). 0 disables this type.' },
		],
	},
	{
		title: 'Center forces',
		items: [
			{ key: 'centerRepelStrength', def: 0, min: 0, max: 0.05, step: 0.001, tip: 'Outward push from the center to drive particles toward the edges. Higher = more pronounced hollow-center / ring effect.' },
			{ key: 'centerRepelFalloff', def: 1.5, min: 0, max: 3, step: 0.05, tip: 'Radius (fraction of circle radius) within which the center repulsion applies.' },
			{ key: 'centerOrbitStrength', def: 0.02, min: 0, max: 0.1, step: 0.002, tip: 'Tangential force around the center, making particles orbit. Strength scales with distance across the plateau (centerOrbitPlateau) so angular velocity stays uniform there (no whip-fast spin at the exact center), then quadratically fades to zero by the falloff edge.' },
			{ key: 'centerOrbitFalloff', def: 2, min: 0, max: 3, step: 0.05, tip: 'Radius (fraction of circle radius) within which the center orbit force applies.' },
			{ key: 'centerOrbitPlateau', def: 0.7, min: 0, max: 1, step: 0.05, tip: 'Fraction (0–1) of the orbit-falloff radius across which strength ramps linearly with distance (uniform angular velocity). Past the plateau, strength quadratically fades to zero by the falloff edge. 1.0 = no fade-out, pure ramp; 0 = no plateau, pure falloff from center.' },
		],
	},
	{
		title: 'Boundary & separation',
		items: [
			{ key: 'boundaryForceStart', def: 0.95, min: 0.5, max: 1, step: 0.01, tip: 'Distance from center (fraction of radius) where the optional inward boundary force begins. 0.95 = force kicks in at 95% of the radius.' },
			{ key: 'boundaryForceStrength', def: 0, min: 0, max: 0.2, step: 0.005, tip: 'Strength of the optional inward boundary push. 0 = disabled (default); the circle is a soft fade boundary, not a wall. Higher = harder wall.' },
			{ key: 'respawnOuterRadiusFraction', def: 0.20, min: 0.01, max: 1, step: 0.01, tip: 'Outer radius (fraction of the layer radius) of the band where destroyed particles respawn after fading out past the circle edge. With respawnInnerRadiusFraction at 0 this is a solid central disc.' },
			{ key: 'respawnInnerRadiusFraction', def: 0, min: 0, max: 1, step: 0.01, tip: 'Fraction of the radius defining a central void where particles never spawn or respawn — spawning happens in the annulus between this and respawnOuterRadiusFraction. 0 = no void (solid disc). Pair with a positive centerRepelStrength so particles can\'t drift back into the void.' },
			{ key: 'particleFadeDuration', def: 0.5, min: 0, max: 3, step: 0.05, tip: 'Seconds for a particle to shrink to nothing once it crosses the circle edge, and to grow back from zero once it respawns at the center.' },
			{ key: 'separationRadius', def: 4.5, min: 0, max: 20, step: 0.5, tip: 'Distance (CSS px) within which particles repel each other. 0 disables separation.' },
			{ key: 'separationStrength', def: 0.04, min: 0, max: 0.2, step: 0.005, tip: 'Strength of the short-range particle separation force.' },
		],
	},
	{
		title: 'Density grid',
		items: [
			{ key: 'densityGridCells', def: 20, min: 5, max: 50, step: 1, tip: 'Number of grid cells per axis used to estimate local density (total cells = N²). Higher = finer resolution but more overhead.' },
			{ key: 'densityUpdateInterval', def: 10, min: 1, max: 60, step: 1, tip: 'Recalculate the density grid every N frames. Lower = more responsive size changes but higher per-frame cost.' },
			{ key: 'radiusLerpSpeed', def: 3, min: 0.1, max: 20, step: 0.1, tip: 'How quickly particle radii lerp toward their density target per second. Higher = snappier, lower = smoother.' },
			{ key: 'densityMin', def: 2, min: 0, max: 30, step: 1, tip: 'Cells with this many particles or fewer map to minRadius.' },
			{ key: 'densityMax', def: 15, min: 1, max: 50, step: 1, tip: 'Cells with this many particles or more map to maxRadius. Cells in between are linearly interpolated.' },
		],
	},
];

/**
 * Resolve the initial value for a control — what the slider starts at and
 * what Reset returns to. Prefers LAYER_OVERRIDES (project-level preset)
 * over LAYER_DEFAULTS (framework defaults) over the per-control fallback.
 * Locus-weight controls map to the matching entry in lociWeights.
 * @param {string} key
 * @param {number} fallback
 * @return {number}
 */
function resolve_initial(key, fallback) {
	if (key.startsWith('_weight_')) {
		const type = key.slice('_weight_'.length);
		const list = (LAYER_OVERRIDES && LAYER_OVERRIDES.lociWeights)
			|| LAYER_DEFAULTS.lociWeights || [];
		const entry = list.find(e => e.type === type);
		return entry && entry.weight != null ? entry.weight : fallback;
	}
	if (LAYER_OVERRIDES && LAYER_OVERRIDES[key] != null) return LAYER_OVERRIDES[key];
	if (LAYER_DEFAULTS[key] != null) return LAYER_DEFAULTS[key];
	return fallback;
}


/**
 * Resolve the pristine framework default for a control — used for
 * "Show JS" diffing so the dump emits any value that differs from
 * LAYER_DEFAULTS, including project-level LAYER_OVERRIDES tweaks. Pasting
 * the dump back into LAYER_OVERRIDES reproduces the current state.
 * @param {string} key
 * @param {number} fallback
 * @return {number}
 */
function resolve_pristine(key, fallback) {
	if (key.startsWith('_weight_')) {
		const type = key.slice('_weight_'.length);
		const list = LAYER_DEFAULTS.lociWeights || [];
		const entry = list.find(e => e.type === type);
		return entry && entry.weight != null ? entry.weight : fallback;
	}
	if (LAYER_DEFAULTS[key] != null) return LAYER_DEFAULTS[key];
	return fallback;
}


const state = {};
for (const g of groups) {
	for (const c of g.items) {
		state[c.key] = resolve_initial(c.key, c.def);
	}
}


/**
 * Build the override object passed to applyOverrides()
 */
function build_overrides() {
	const o = {};
	for (const g of groups) {
		for (const c of g.items) {
			if (c.key.startsWith('_')) continue;
			o[c.key] = state[c.key];
		}
	}
	o.lociWeights = weight_keys.map(t => ({
		type: t,
		weight: state[`_weight_${t}`],
	}));
	return o;
}


/**
 * Apply current state to every cloud canvas on the page
 */
function apply_all() {
	const overrides = build_overrides();
	document.querySelectorAll(canvas_$).forEach($el => {
		const field = $el._particleField;
		if (field && typeof field.applyOverrides === 'function') {
			field.applyOverrides(overrides);
		}
	});
}


/**
 * Format a number for display next to its slider
 */
function fmt(v) {
	if (Number.isInteger(v)) return String(v);
	const abs = Math.abs(v);
	if (abs >= 1) return v.toFixed(2);
	if (abs >= 0.01) return v.toFixed(3);
	if (abs >= 0.001) return v.toFixed(4);
	return v.toFixed(5);
}


/**
 * Render the JS-paste output: only show keys whose value differs
 * from default. Includes lociWeights only if any weight changed.
 */
function build_js_dump() {
	const lines = [];
	for (const g of groups) {
		for (const c of g.items) {
			if (c.key.startsWith('_')) continue;
			if (state[c.key] !== resolve_pristine(c.key, c.def)) {
				lines.push(`${c.key}: ${state[c.key]},`);
			}
		}
	}
	const weights_changed = weight_keys.some(t => {
		const item = groups.find(g => g.title === 'Locus type weights').items.find(i => i.key === `_weight_${t}`);
		return state[`_weight_${t}`] !== resolve_pristine(item.key, item.def);
	});
	if (weights_changed) {
		const ws = weight_keys.map(t => `\t{ type: '${t}', weight: ${state[`_weight_${t}`]} }`).join(',\n');
		lines.push(`lociWeights: [\n${ws},\n],`);
	}
	return lines.length ? lines.join('\n') : '// no changes from defaults';
}


/**
 * Copy text to the clipboard. Tries the async Clipboard API first
 * (requires a secure context and clipboard-write permission), then
 * falls back to a hidden textarea + execCommand for embedded /
 * insecure contexts. Resolves true on success, false on failure.
 * @param {string} text
 * @return {Promise<boolean>}
 */
function copy_text(text) {
	if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
		return navigator.clipboard.writeText(text).then(() => true, () => fallback_copy(text));
	}
	return Promise.resolve(fallback_copy(text));
}


/**
 * Synchronous clipboard fallback using a temporary textarea
 * @param {string} text
 * @return {boolean}
 */
function fallback_copy(text) {
	const $ta = document.createElement('textarea');
	$ta.value = text;
	$ta.setAttribute('readonly', '');
	$ta.style.position = 'fixed';
	$ta.style.top = '0';
	$ta.style.left = '0';
	$ta.style.opacity = '0';
	document.body.appendChild($ta);
	$ta.select();
	let ok = false;
	try {
		ok = document.execCommand('copy');
	} catch (err) {
		ok = false;
	}
	document.body.removeChild($ta);
	return ok;
}


/**
 * Build the panel DOM and wire up events
 */
function build_panel() {
	const $panel = document.createElement('aside');
	$panel.className = panel_class;
	$panel.innerHTML = `
		<header class="${panel_class}__header">
			<strong>Cloud controls</strong>
			<div class="${panel_class}__header-actions">
				<button type="button" data-action="reset" title="Reset all to defaults">Reset</button>
				<button type="button" data-action="dump" title="Show changed values as JS">Show JS</button>
				<button type="button" data-action="toggle" title="Collapse" aria-label="Collapse">−</button>
			</div>
		</header>
		<label class="${panel_class}__toggle" title="Overlay each locus's position, type, and influence radius (linear falloff to the edge of that radius)">
			<input type="checkbox" data-action="debug">
			<span>Visualize loci (position, type, influence radius)</span>
		</label>
		<div class="${panel_class}__body"></div>
		<div class="${panel_class}__dump" hidden>
			<pre><code></code></pre>
			<button type="button" data-action="copy">Copy</button>
		</div>
	`;

	const $body = $panel.querySelector(`.${panel_class}__body`);

	for (const g of groups) {
		const $group = document.createElement('details');
		$group.className = `${panel_class}__group`;
		$group.open = true;
		$group.innerHTML = `<summary>${g.title}</summary>`;

		for (const c of g.items) {
			const $row = document.createElement('label');
			$row.className = `${panel_class}__row`;
			const tip = c.tip ? c.tip.replace(/"/g, '&quot;') : '';
			const start = resolve_initial(c.key, c.def);
			$row.innerHTML = `
				<span class="${panel_class}__key" title="${tip}">${c.key}</span>
				<input type="range" min="${c.min}" max="${c.max}" step="${c.step}" value="${start}" data-key="${c.key}" title="${tip}">
				<output>${fmt(start)}</output>
			`;
			const $input = $row.querySelector('input');
			const $out = $row.querySelector('output');
			$input.addEventListener('input', () => {
				const v = parseFloat($input.value);
				state[c.key] = v;
				$out.textContent = fmt(v);
				apply_all();
			});
			$group.appendChild($row);
		}

		$body.appendChild($group);
	}

	const $dump = $panel.querySelector(`.${panel_class}__dump`);
	const $dump_code = $dump.querySelector('code');

	const $debug = $panel.querySelector('input[data-action="debug"]');
	$debug.addEventListener('change', () => {
		const on = $debug.checked;
		document.querySelectorAll(canvas_$).forEach($el => {
			const field = $el._particleField;
			if (field && typeof field.setDebug === 'function') {
				field.setDebug(on);
			}
		});
	});

	$panel.addEventListener('click', e => {
		const action = e.target.getAttribute && e.target.getAttribute('data-action');
		if (!action) return;

		if (action === 'reset') {
			for (const g of groups) {
				for (const c of g.items) {
					const v = resolve_initial(c.key, c.def);
					state[c.key] = v;
					const $i = $panel.querySelector(`input[data-key="${c.key}"]`);
					if ($i) {
						$i.value = v;
						$i.nextElementSibling.textContent = fmt(v);
					}
				}
			}
			apply_all();
			if (!$dump.hidden) $dump_code.textContent = build_js_dump();
		}

		if (action === 'dump') {
			$dump_code.textContent = build_js_dump();
			$dump.hidden = !$dump.hidden;
		}

		if (action === 'copy') {
			const $btn = e.target;
			copy_text($dump_code.textContent).then(ok => {
				$btn.textContent = ok ? 'Copied' : 'Copy failed';
				setTimeout(() => { $btn.textContent = 'Copy'; }, 1200);
			});
		}

		if (action === 'toggle') {
			$panel.classList.toggle('is-collapsed');
			e.target.textContent = $panel.classList.contains('is-collapsed') ? '+' : '−';
		}
	});

	document.body.appendChild($panel);
}


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('particle-cloud-demo')) return false;

		load.all(() => {
			if (!document.querySelector(page_$)) return false;
			if (!document.querySelector(canvas_$)) return false;
			build_panel();
		});
	},

};

export default module;
module.init();
